import { AnimatePresence, motion } from "framer-motion";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

interface ProviderProgressActionMenuProps {
  onOpenInspector: () => void;
  onOpenTranscript: () => void;
  transcriptDisabled?: boolean;
}

const VerticalEllipsisIcon: FC = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="currentColor"
    viewBox="0 0 20 20"
    aria-hidden="true"
  >
    <path d="M10 4.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
  </svg>
);

const EyeIcon: FC = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const TranscriptIcon: FC = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M7 8h10M7 12h10M7 16h6M6 3h12a2 2 0 012 2v14l-4-2-4 2-4-2-4 2V5a2 2 0 012-2z"
    />
  </svg>
);

const MenuActionButton: FC<{
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}> = ({ disabled = false, icon, label, onClick }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    onClick={onClick}
    className="flex w-full items-center gap-2 border-b border-slate-200 px-3 py-2 text-left text-xs text-slate-600 transition-colors last:border-b-0 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
  >
    <span className="text-slate-400 dark:text-slate-500">{icon}</span>
    <span className="font-mono font-bold uppercase tracking-[0.16em]">
      {label}
    </span>
  </button>
);

export const ProviderProgressActionMenu: FC<
  ProviderProgressActionMenuProps
> = ({ onOpenInspector, onOpenTranscript, transcriptDisabled = false }) => {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const { target } = event;
      if (!(target instanceof Node)) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleSelect = useCallback((action: () => void) => {
    setOpen(false);
    action();
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <motion.button
        type="button"
        whileTap={{ scale: 0.96 }}
        onClick={() => setOpen((current) => !current)}
        className="group relative flex h-8 w-8 shrink-0 items-center justify-center border border-slate-600 bg-arti-background text-slate-400 transition-all duration-200 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-400 sm:h-6 sm:w-6"
        title="Stream actions"
        aria-label="Stream actions"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
      >
        <VerticalEllipsisIcon />
        <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-emerald-500 transition-all duration-300 group-hover:w-full" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={menuId}
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.16 }}
            className="absolute left-0 top-full z-20 mt-2 w-[min(13rem,calc(100vw-2rem))] overflow-hidden border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="border-b border-slate-200 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Stream actions
            </div>
            <MenuActionButton
              icon={<EyeIcon />}
              label="Inspect prompts"
              onClick={() => handleSelect(onOpenInspector)}
            />
            <MenuActionButton
              disabled={transcriptDisabled}
              icon={<TranscriptIcon />}
              label="View transcript"
              onClick={() => handleSelect(onOpenTranscript)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
